import type { FC } from 'react';
import { CustomSubAccountIconEnum } from 'types/banks.types';

// Import actual SVG files from spacesBanking folder
import ArchiveStackSvg from '../../../assets/svg/spacesBanking/archive-stack.svg?react';
import BankrollSvg from '../../../assets/svg/spacesBanking/bankroll.svg?react';
import CakeSliceSvg from '../../../assets/svg/spacesBanking/cake-slice.svg?react';
import CheckmarkSvg from '../../../assets/svg/spacesBanking/checkmark.svg?react';
import ChocolateSvg from '../../../assets/svg/spacesBanking/chocolate.svg?react';
import ColorPaletteSvg from '../../../assets/svg/spacesBanking/color-palette.svg?react';
import DiamondSvg from '../../../assets/svg/spacesBanking/diamond.svg?react';
import GiftSvg from '../../../assets/svg/spacesBanking/gift.svg?react';
import HeartSvg from '../../../assets/svg/spacesBanking/heart.svg?react';
import LaptopSvg from '../../../assets/svg/spacesBanking/laptop.svg?react';
import LightbulbSvg from '../../../assets/svg/spacesBanking/light-bulb.svg?react';
import OfficeChairSvg from '../../../assets/svg/spacesBanking/office-chair.svg?react';
import PenWriteSvg from '../../../assets/svg/spacesBanking/pen-write.svg?react';
import PiggySvg from '../../../assets/svg/spacesBanking/piggy-saving.svg?react';
import PlantSvg from '../../../assets/svg/spacesBanking/plant.svg?react';
import TargetSimpleSvg from '../../../assets/svg/spacesBanking/target-simple.svg?react';
import TargetSvg from '../../../assets/svg/spacesBanking/target.svg?react';
import UnlockSvg from '../../../assets/svg/spacesBanking/unlock.svg?react';

interface IconProps {
  className?: string;
}

// Wrapper components that use the actual SVG files
export const GlassesIcon: FC<IconProps> = ({ className }) => (
  <CheckmarkSvg className={className} />
);

export const MoneyIcon: FC<IconProps> = ({ className }) => (
  <BankrollSvg className={className} />
);

export const ChairIcon: FC<IconProps> = ({ className }) => (
  <OfficeChairSvg className={className} />
);

export const CakeIcon: FC<IconProps> = ({ className }) => (
  <CakeSliceSvg className={className} />
);

export const HeartIcon: FC<IconProps> = ({ className }) => (
  <HeartSvg className={className} />
);

export const LaptopIcon: FC<IconProps> = ({ className }) => (
  <LaptopSvg className={className} />
);

export const PresentIcon: FC<IconProps> = ({ className }) => (
  <GiftSvg className={className} />
);

export const DiamondIcon: FC<IconProps> = ({ className }) => (
  <DiamondSvg className={className} />
);

export const PlantIcon: FC<IconProps> = ({ className }) => (
  <PlantSvg className={className} />
);

export const ChocolateIcon: FC<IconProps> = ({ className }) => (
  <ChocolateSvg className={className} />
);

export const PiggyIcon: FC<IconProps> = ({ className }) => (
  <PiggySvg className={className} />
);

export const PenWriteIcon: FC<IconProps> = ({ className }) => (
  <PenWriteSvg className={className} />
);

export const ColorPaletteIcon: FC<IconProps> = ({ className }) => (
  <ColorPaletteSvg className={className} />
);

export const TargetIcon: FC<IconProps> = ({ className }) => (
  <TargetSvg className={className} />
);

export const ArchiveStackIcon: FC<IconProps> = ({ className }) => (
  <ArchiveStackSvg className={className} />
);

export const TargetSimpleIcon: FC<IconProps> = ({ className }) => (
  <TargetSimpleSvg className={className} />
);

export const LightbulbIcon: FC<IconProps> = ({ className }) => (
  <LightbulbSvg className={className} />
);

export const UnlockIcon: FC<IconProps> = ({ className }) => (
  <UnlockSvg className={className} />
);

// Shared ICON_OPTIONS array for use across components
export const ICON_OPTIONS: Array<{
  id: CustomSubAccountIconEnum;
  label: CustomSubAccountIconEnum;
  icon: React.ReactNode;
}> = [
  {
    id: CustomSubAccountIconEnum.GLASSES,
    label: CustomSubAccountIconEnum.GLASSES,
    icon: <GlassesIcon />,
  },
  {
    id: CustomSubAccountIconEnum.MONEY,
    label: CustomSubAccountIconEnum.MONEY,
    icon: <MoneyIcon />,
  },
  {
    id: CustomSubAccountIconEnum.CHAIR,
    label: CustomSubAccountIconEnum.CHAIR,
    icon: <ChairIcon />,
  },
  {
    id: CustomSubAccountIconEnum.CAKE,
    label: CustomSubAccountIconEnum.CAKE,
    icon: <CakeIcon />,
  },
  {
    id: CustomSubAccountIconEnum.HEART,
    label: CustomSubAccountIconEnum.HEART,
    icon: <HeartIcon />,
  },
  {
    id: CustomSubAccountIconEnum.LAPTOP,
    label: CustomSubAccountIconEnum.LAPTOP,
    icon: <LaptopIcon />,
  },
  {
    id: CustomSubAccountIconEnum.PRESENT,
    label: CustomSubAccountIconEnum.PRESENT,
    icon: <PresentIcon />,
  },
  {
    id: CustomSubAccountIconEnum.DIAMOND,
    label: CustomSubAccountIconEnum.DIAMOND,
    icon: <DiamondIcon />,
  },
  {
    id: CustomSubAccountIconEnum.PLANT,
    label: CustomSubAccountIconEnum.PLANT,
    icon: <PlantIcon />,
  },
  {
    id: CustomSubAccountIconEnum.CHOCOLATE,
    label: CustomSubAccountIconEnum.CHOCOLATE,
    icon: <ChocolateIcon />,
  },
];

// Utility function to render custom space icons
export const renderCustomSubAccountIcon = (
  iconType?: CustomSubAccountIconEnum,
  className?: string,
): React.ReactNode | null => {
  const props = { className };

  switch (iconType) {
    case 'glasses':
      return <GlassesIcon {...props} />;
    case 'money':
      return <MoneyIcon {...props} />;
    case 'chair':
      return <ChairIcon {...props} />;
    case 'cake':
      return <CakeIcon {...props} />;
    case 'heart':
      return <HeartIcon {...props} />;
    case 'laptop':
      return <LaptopIcon {...props} />;
    case 'present':
      return <PresentIcon {...props} />;
    case 'diamond':
      return <DiamondIcon {...props} />;
    case 'plant':
      return <PlantIcon {...props} />;
    case 'chocolate':
      return <ChocolateIcon {...props} />;
    default:
      return null;
  }
};
